import _ from 'underscore';
import { mergeClasses } from '@fluentui/react-components';
import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  VideosFilterBar,
  FilterContext,
  FolderAssetType,
  AdsStudioSpinner,
  CSMLVideoDuration,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { usePageActiveTimeTracker } from '@bingads-webui-campaign-react/common-hooks';

import { VideoPageTopBannerComponent, VideoEmptyView, VideoGridView } from './components';
import { useVideoPageStyle } from '../styles';

export const VideosPageComponent = ({
  i18n,
  logger,
  adsStudioUserPreferences,
  aid,
  cid,
  oDataConfig,
  dataService,
  notificationApi,
  navigation,
  reload,
  pagePerfMarker: videoPagePerfMarker,
  permissions,
  setDataLoaded,
}) => {
  const classes = useVideoPageStyle();

  const {
    totalAssetCount,
    loadData,
  } = dataService;

  const [isPageInited, setPageInited] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  const { filterBar } = useContext(FilterContext);

  const {
    searchText,
    sortBy,
    minDurationFilter,
    maxDurationFilter,
  } = useMemo(() => filterBar.getFilters(), [filterBar]);

  const onFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load videos. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  // init page
  useEffect(() => {
    if (!isPageInited) {
      setPageInited(true);
    }
  }, [isPageInited]);

  usePageActiveTimeTracker(logger, 'Video');

  // load data for initial page load
  useEffect(() => {
    if (!isPageInited) {
      // wait until page is initialized
      return;
    }

    if (reload) { // Important! Add a trivial log so that reload will be an explicit dep.
      logger.logTrace('force to reload');
    }
    videoPagePerfMarker.willFetchData();
    setLoadingData(true);
    loadData({
      assetTypeFilter: FolderAssetType.Image,
      isGlobalSearch: true,
      searchText,
      sortBy,
      perfMarker: videoPagePerfMarker.createChild('videoPageLoadData'),
      isVideoDurationBoundaryIncluded: true,
      minVideoDuration: minDurationFilter,
      maxVideoDuration: maxDurationFilter > CSMLVideoDuration.max ? null : maxDurationFilter, // when maxDurationFilter is over max value, remove the maxVideoDuration filter
    })
      .catch(() => {
        onFailedToLoadData();
      }).finally(() => {
        setLoadingData(false);
        videoPagePerfMarker.willRender();
        if (setDataLoaded) {
          setDataLoaded();
        }
      });
  }, [isPageInited, loadData, onFailedToLoadData, searchText, sortBy, reload, logger, minDurationFilter, maxDurationFilter]);

  const isPageLoading = !isPageInited || loadingData;
  const isFilterResult = useMemo(() => (
    !_.isEmpty(searchText)
    || !(minDurationFilter === CSMLVideoDuration.min && maxDurationFilter === CSMLVideoDuration.unlimited)
  ), [maxDurationFilter, minDurationFilter, searchText]);

  return (
    <div className={mergeClasses('video-page-container', classes.videoPage)}>
      {isPageLoading && (
        <Overlay>
          <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={{ margin: 200 }} />
        </Overlay>
      )}
      <VideoPageTopBannerComponent
        i18n={i18n}
        aid={aid}
        cid={cid}
        oDataConfig={oDataConfig}
        navigation={navigation}
        logger={logger}
        permissions={permissions}
      />
      <VideosFilterBar
        i18n={i18n}
        logger={logger}
        searchboxPlaceHolder={i18n.getString(_TL_('Search by asset name or ID'))}
        userPreference={adsStudioUserPreferences}
        defaultMinVideoDuration={CSMLVideoDuration.min}
        defaultMaxVideoDuration={CSMLVideoDuration.unlimited}
      />
      {
        totalAssetCount && totalAssetCount > 0 ?
          <VideoGridView
            i18n={i18n}
            logger={logger}
            dataService={dataService}
            onFailedToLoadData={onFailedToLoadData}
            searchText={searchText}
            navigation={navigation}
            permissions={permissions}
          /> :
          <VideoEmptyView i18n={i18n} isFilterResult={isFilterResult} />
      }
    </div>
  );
};

VideosPageComponent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  dataService: PropTypes.shape({}),
  notificationApi: PropTypes.shape({}),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  adsStudioUserPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}),
  navigation: PropTypes.shape({}).isRequired,
};

VideosPageComponent.defaultProps = {
  dataService: {},
  notificationApi: {},
  adsStudioUserPreferences: undefined,
  reload: null,
};
